import api from "../utils/api";
import { ApiResponse } from "../utils/api/ApiProps";

export interface CSVParseResponse {
  headers: string[];
  preview: Record<string, any>[];
  totalRows: number;
  data: Record<string, any>[];
}

export interface ColumnMappingConfig {
  [csvColumn: string]: {
    col_id: string; // UUID of the CRM column
    col_name?: string;
  };
}

export interface ImportBatchResponse {
  success: number;
  failed: number;
  skipped: string[] | Array<{ row: number; reason: string; phone?: string }>;
  errors: string[];
}

export interface ValidationResponse {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  mappedFields: string[];
  mappedColumns: Array<{
    uuid: string;
    name: string;
    label: string;
    type: string;
  }>;
}

export interface AvailableColumn {
  col_id: number;
  col_name: string;
  col_label: string;
  col_type: string;
  uuid: string;
  options?: Array<{
    opt_id: number;
    opt_name: string;
    opt_value: string;
    uuid: string;
  }>;
}

/**
 * Upload and parse CSV file
 * @param file - CSV file to upload
 */
export const uploadCSV = async (file: File): Promise<ApiResponse<CSVParseResponse>> => {
  const formData = new FormData();
  formData.append('csvFile', file);

  // Don't manually set Content-Type for FormData - let axios/browser set it with boundary
  const response = await api.req<CSVParseResponse>({
    url: '/import/upload',
    method: api.Method.POST,
    body: formData,
  });

  return response;
};

/**
 * Get available CRM columns for mapping
 */
export const getAvailableColumns = async (): Promise<ApiResponse<AvailableColumn[]>> => {
  const response = await api.get<AvailableColumn[]>('/import/columns');
  return response;
};

/**
 * Validate column mapping configuration
 * @param mapping - Mapping configuration object
 */
export const validateMapping = async (
  mapping: ColumnMappingConfig
): Promise<ApiResponse<ValidationResponse>> => {
  const response = await api.req<ValidationResponse>({
    url: '/import/validate-mapping',
    method: api.Method.POST,
    body: { mapping },
  });

  return response;
};

/**
 * Execute batch import
 * @param data - Array of lead data objects from CSV
 * @param mapping - Column mapping configuration
 */
export const executeImport = async (
  data: Record<string, any>[],
  mapping: ColumnMappingConfig
): Promise<ApiResponse<ImportBatchResponse>> => {
  const response = await api.req<ImportBatchResponse>({
    url: '/import/batch',
    method: api.Method.POST,
    body: {
      data,
      mapping,
    },
  });

  return response;
};

/**
 * Get import history
 */
export const getImportHistory = async (): Promise<ApiResponse<any[]>> => {
  const response = await api.get<any[]>('/import/history');
  return response;
};
